import Button from '@blaze-react/button';
import { useToasts } from '@blaze-react/toaster';
import Select from '@blaze-react/select';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { PRODUCT_SUPERPORTS_LISTING } from './constants';
import {
  pushToDataLayer,
  submitSalesforceLead,
  checkValidEmail,
  RESIDENCE_COUNTRIES
} from '../../utils';
import { PRIVACY } from '../../constants';

const countryOfResidenceOptions = [
  ['', '* What is your country of residence?'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const SuperportLeadForm = ({ description, closeModal, modalTitle }) => {
  const { addToast } = useToasts();
  const [submitState, setSubmitState] = useState(null);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [additionalTermsAccepted, setAdditionalTermsAccepted] = useState(null);

  const [emailError, setEmailError] = useState(null);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [companyError, setCompanyError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [termsAcceptedError, setTermsAcceptedError] = useState(null);
  const [countryOfResidenceError, setCountryOfResidenceError] = useState(null);
  const [additionalTermsAcceptedError, setAdditionalTermsAcceptedError] = useState(null);

  const inputClass = 'salesforce-lead__input';
  const inputWrapperClass = 'salesforce-lead__input-wrapper';
  const errorClass = 'salesforce-lead__error';

  const emailInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!emailError,
    [inputClass]: !emailError
  });

  const firstNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!firstNameError,
    [inputClass]: !firstNameError
  });

  const lastNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!lastNameError,
    [inputClass]: !lastNameError
  });

  const companyInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!companyError,
    [inputClass]: !companyError
  });

  const phoneInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!phoneError,
    [inputClass]: !phoneError
  });

  const termsAcceptedInputClass = classnames({
    'salesforce-lead__checkbox salesforce-lead__checkbox--error': !!termsAcceptedError,
    'salesforce-lead__checkbox': !termsAcceptedError
  });

  const additionalTermsAcceptedInputClass = classnames({
    'salesforce-lead__radio salesforce-lead__radio--error': !!additionalTermsAcceptedError,
    'salesforce-lead__radio': !additionalTermsAcceptedError
  });

  const countryOfResidenceInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!countryOfResidenceError,
    [inputClass]: !countryOfResidenceError
  });

  const handleKeyDown = event => {
    if (event.keyCode !== 13) return;

    setSubmitState({ target: event.target });
  };

  const submit = async (
    passedEmail,
    passedFirstName,
    passedLastName,
    passedCompany,
    passedPhone,
    passedCountryOfResidence,
    passedAdditionalTermsAccepted
  ) => {
    try {
      await submitSalesforceLead(
        passedEmail,
        PRODUCT_SUPERPORTS_LISTING,
        passedFirstName,
        passedLastName,
        passedCompany,
        null,
        passedCountryOfResidence,
        passedPhone,
        null,
        null,
        null,
        passedAdditionalTermsAccepted
      );

      addToast('You successfully submitted the form', {
        appearance: 'success',
        autoDismiss: true
      });

      pushToDataLayer({ event: 'Lead Generated', leadSource: PRODUCT_SUPERPORTS_LISTING });

      return true;
    } catch {
      addToast('Error while submitting the form, please try again', {
        appearance: 'error',
        autoDismiss: true
      });

      return false;
    }
  };

  useEffect(
    () => {
      if (submitState === null) return;

      let valid = true;
      if (!email) {
        setEmailError('Required field');
        valid = false;
      } else if (!checkValidEmail(email)) {
        setEmailError('Invalid email format');
        valid = false;
      }
      if (!firstName) {
        setFirstNameError('Required field');
        valid = false;
      }
      if (!lastName) {
        setLastNameError('Required field');
        valid = false;
      }
      if (!company) {
        setCompanyError('Required field');
        valid = false;
      }
      if (!phone) {
        setPhoneError('Required field');
        valid = false;
      }
      if (!termsAccepted) {
        setTermsAcceptedError('Please accept the terms and conditions');
        valid = false;
      }
      if (!RESIDENCE_COUNTRIES.includes(countryOfResidence)) {
        setCountryOfResidenceError('Required field');
        valid = false;
      }
      if (additionalTermsAccepted === null) {
        setAdditionalTermsAcceptedError('Please select YES or NO on the additional terms');
        valid = false;
      }
      if (!valid) {
        setSubmitState(null);
        return;
      }

      if (submitState.target) {
        submitState.target.blur();
      }

      (async () => {
        const successful = await submit(
          email,
          firstName,
          lastName,
          company,
          phone,
          countryOfResidence,
          additionalTermsAccepted
        );

        setSubmitState(null);

        if (successful) {
          setEmail('');
          setFirstName('');
          setLastName('');
          setCompany('');
          setPhone('');
          setTermsAccepted(false);
          setCountryOfResidence('');
          setAdditionalTermsAccepted(null);
          closeModal();
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      submitState,
      email,
      firstName,
      lastName,
      company,
      phone,
      countryOfResidence,
      additionalTermsAccepted
    ]
  );

  return (
    <div className="overlay">
      <div className="salesforce-lead modal modal--contact modal--show">
        <header className="modal__header modal__header--contact">
          <div className="modal__title">{modalTitle}</div>

          <div
            role="button"
            className="modal__close"
            onClick={closeModal}
            data-testid="close-button">
            <i>
              <FaTimes />
            </i>
          </div>
        </header>

        {submitState && <div className="salesforce-lead__overlay" />}

        <div className="salesforce-lead__fields-wrapper">
          <div className="salesforce-lead__description">{description}</div>

          <div className={inputWrapperClass}>
            <input
              className={emailInputClass}
              type="text"
              value={email}
              onChange={event => setEmail(event.target.value)}
              onKeyDown={event => {
                setEmailError(null);
                handleKeyDown(event);
              }}
              placeholder="*Your email"
            />

            {emailError && <p className={errorClass}>{emailError}</p>}
          </div>

          <div className="salesforce-lead__name-wrapper">
            <div className={inputWrapperClass}>
              <input
                className={firstNameInputClass}
                type="text"
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                onKeyDown={event => {
                  setFirstNameError(null);
                  handleKeyDown(event);
                }}
                placeholder="*First Name"
              />

              {firstNameError && <p className={errorClass}>{firstNameError}</p>}
            </div>

            <div className={inputWrapperClass}>
              <input
                className={lastNameInputClass}
                type="text"
                value={lastName}
                onChange={event => setLastName(event.target.value)}
                onKeyDown={event => {
                  setLastNameError(null);
                  handleKeyDown(event);
                }}
                placeholder="*Last Name"
              />

              {lastNameError && <p className={errorClass}>{lastNameError}</p>}
            </div>
          </div>

          <div className={inputWrapperClass}>
            <input
              className={companyInputClass}
              type="text"
              value={company}
              onChange={event => setCompany(event.target.value)}
              onKeyDown={event => {
                setCompanyError(null);
                handleKeyDown(event);
              }}
              placeholder="*Company / Marina name"
            />

            {companyError && <p className={errorClass}>{companyError}</p>}
          </div>

          <div className={inputWrapperClass}>
            <input
              className={phoneInputClass}
              type="text"
              value={phone}
              onChange={event => setPhone(event.target.value)}
              onKeyDown={event => {
                setPhoneError(null);
                handleKeyDown(event);
              }}
              placeholder="*Phone number"
            />

            {phoneError && <p className={errorClass}>{phoneError}</p>}
          </div>

          <div className={inputWrapperClass}>
            <Select
              name="countryOfResidence"
              className={countryOfResidenceInputClass}
              options={countryOfResidenceOptions}
              onChange={({ value }) => {
                setCountryOfResidenceError(null);
                setCountryOfResidence(value);
              }}
              selected={countryOfResidence}
            />

            {countryOfResidenceError && <p className={errorClass}>{countryOfResidenceError}</p>}
          </div>

          <div className={termsAcceptedInputClass}>
            <input
              readOnly
              className="salesforce-lead__checkbox__box"
              type="checkbox"
              checked={termsAccepted}
              name="termsAccepted"
              onClick={() => {
                setTermsAcceptedError(null);
                setTermsAccepted(!termsAccepted);
              }}
            />

            <div
              className="salesforce-lead__checkbox__box-label"
              onClick={() => {
                setTermsAcceptedError(null);
                setTermsAccepted(!termsAccepted);
              }}
            />

            <label
              className="salesforce-lead__checkbox__label"
              onClick={() => {
                setTermsAcceptedError(null);
                setTermsAccepted(!termsAccepted);
              }}>
              I agree to the{' '}
              <a
                href={PRIVACY.TERMS_HREF}
                className="salesforce-lead__checkbox__link"
                target="_blank"
                rel="noopener noreferrer">
                terms and conditions
              </a>{' '}
              and{' '}
              <a
                href={PRIVACY.HREF}
                className="salesforce-lead__checkbox__link"
                target="_blank"
                rel="noopener noreferrer">
                privacy notice
              </a>
              .
            </label>
          </div>

          <div className={additionalTermsAcceptedInputClass}>
            <label className="salesforce-lead__radio__label">
              I would like to receive tailored information about events and products from BOAT
              International Media Ltd, an Informa Group Company. You can opt out at any time by
              clicking unsubscribe or contacting us. For Further details, see our{' '}
              <a
                href={PRIVACY.HREF}
                className="salesforce-lead__checkbox__footer-link"
                target="_blank"
                rel="noopener noreferrer">
                privacy notice
              </a>
              .
            </label>

            <div className="salesforce-lead__radio__boxes">
              <input
                readOnly
                type="radio"
                name="additionalTermsAccepted"
                value="yes"
                className="salesforce-lead__radio__box"
                checked={additionalTermsAccepted === true}
                onClick={() => setAdditionalTermsAccepted(true)}
              />
              <label
                className="salesforce-lead__radio__box-label"
                onClick={() => setAdditionalTermsAccepted(true)}>
                Yes
              </label>{' '}
              <input
                readOnly
                type="radio"
                name="additionalTermsAccepted"
                value="no"
                className="salesforce-lead__radio__box"
                checked={additionalTermsAccepted === false}
                onClick={() => setAdditionalTermsAccepted(false)}
              />
              <label
                className="salesforce-lead__radio__box-label"
                onClick={() => setAdditionalTermsAccepted(false)}>
                No
              </label>
            </div>
          </div>

          <Button
            type="button"
            className="button salesforce-lead__submit"
            onClick={() => setSubmitState({})}>
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

SuperportLeadForm.propTypes = {
  description: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  modalTitle: PropTypes.string.isRequired
};

export default SuperportLeadForm;
